(function () {
  const config = window.archiveReviewsConfig || {};
  const root = document.querySelector("[data-archive-reviews]");

  if (!root) {
    return;
  }

  const state = {
    reviews: [],
    myReviews: [],
    myReviewsLoaded: false,
    page: 1,
    perPage: 2,
    filters: {
      rating: "all",
      photos: "all",
      sort: "newest"
    },
    openMenuReviewId: null,
    isLoading: true,
    reviewId: null,
    rating: 0,
    uploadedImages: [],
    imagePreview: {
      images: [],
      index: 0
    },
    expandedReviewIds: new Set(),
    reviewSubmittedSuccessfully: false
  };

  const allowedImageTypes = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/heic",
    "image/heif"
  ];

  const maxImageSizeBytes = 8 * 1024 * 1024;
  const maxImagesPerReview = 4;
  let lastModalTrigger = null;
  let modalCloseTimer = null;

  const imageTypeByExtension = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    webp: "image/webp",
    heic: "image/heic",
    heif: "image/heif"
  };

  const els = {
    average: root.querySelector("[data-review-average]"),
    stars: root.querySelector("[data-review-stars]"),
    count: root.querySelector("[data-review-count]"),
    verifiedStat: root.querySelector("[data-review-verified-stat]"),
    photoStat: root.querySelector("[data-review-photo-stat]"),
    memberStat: root.querySelector("[data-review-member-stat]"),
    list: root.querySelector("[data-review-list]"),
    empty: root.querySelector("[data-review-empty]"),
    guestEmpty: root.querySelector("[data-review-guest-empty]"),
    guestInfo: root.querySelector("[data-review-guest-info]"),
    guestModerationNote: root.querySelector("[data-review-guest-moderation-note]"),
    guestRewardGuide: root.querySelector("[data-review-guest-reward-guide]"),
    toolbar: root.querySelector(".archive-reviews__toolbar"),
    pagination: root.querySelector("[data-review-pagination]"),
    pages: root.querySelector("[data-review-pages]"),
    memberStatus: root.querySelector("[data-review-member-status]"),
    clearFilters: root.querySelector("[data-clear-review-filters]"),
    form: root.querySelector("[data-review-form]"),
    formContent: root.querySelector("[data-review-form-content]"),
    formMessage: root.querySelector("[data-review-form-message]"),
    formSubmitButton: root.querySelector("[data-review-submit-button]"),
    formSuccess: root.querySelector("[data-review-success]"),
    rewardGuide: root.querySelector("[data-review-reward-guide]")
  };

  const getValue = (item, key, fallback = "") => {
    if (!item || typeof item !== "object") {
      return fallback;
    }

    const pascal = key.charAt(0).toUpperCase() + key.slice(1);
    return item[key] ?? item[pascal] ?? fallback;
  };

  const escapeHtml = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const formatDate = (value) => {
    if (!value) {
      return "";
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return date.toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  const starString = (rating) => {
    const value = Math.max(0, Math.min(5, Number(rating) || 0));
    const full = Math.floor(value);
    const hasHalf = value - full >= 0.5 && full < 5;
    const empty = Math.max(0, 5 - full - (hasHalf ? 1 : 0));

    return "\u2605".repeat(full) + (hasHalf ? "\u2be8" : "") + "\u2606".repeat(empty);
  };

  const getReviewImages = (review) => {
    const images = getValue(review, "images", []);
    return Array.isArray(images) ? images : [];
  };

  const getReviewImageUrl = (image) =>
    getValue(image, "imageUrl", getValue(image, "url", ""));

  const isVerifiedReview = (review) =>
    Boolean(getValue(review, "isVerifiedPurchase", getValue(review, "verifiedPurchase", false)));

  const normaliseReviewValue = (value) =>
    String(value ?? "").trim().toLowerCase();

  const getExplicitReviewSource = (review) => {
    const source = normaliseReviewValue(getValue(review, "reviewSource", ""));

    if (source === "guest") {
      return "guest";
    }

    if (source === "archivemember") {
      return "member";
    }

    return "";
  };

  const getReviewerBadge = (review) => {
    if (isVerifiedReview(review)) {
      return {
        label: "Verified Purchaser",
        modifier: "verified",
        icon: `
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 3.8 14 6l3-.4.8 2.9 2.6 1.5-1.4 2.7 1.4 2.7-2.6 1.5-.8 2.9-3-.4-2 2.2-2-2.2-3 .4-.8-2.9-2.6-1.5 1.4-2.7-1.4-2.7 2.6-1.5.8-2.9 3 .4L12 3.8Z"></path>
            <path d="m8.8 12.3 2.1 2.1 4.5-4.8"></path>
            <path d="M9.2 19.3v2.6l2.8-1.4 2.8 1.4v-2.6"></path>
          </svg>`
      };
    }

    const reviewSource = getExplicitReviewSource(review);

    if (reviewSource === "guest") {
      return {
        label: "Guest Customer",
        modifier: "guest",
        icon: `
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 12.5a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z"></path>
            <path d="M4.8 20.2a7.8 7.8 0 0 1 14.4 0"></path>
          </svg>`
      };
    }

    if (reviewSource === "member") {
      return {
      label: "Archive Member",
      modifier: "member",
      icon: `
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="8.5" r="3.6"></circle>
          <path d="M5.2 20a7.2 7.2 0 0 1 13.6 0"></path>
        </svg>`
      };
    }

    return null;
  };

  const getReviewerPersona = (review) => {
    const persona = String(getValue(review, "reviewerPersona", "") || "").trim();
    const normalised = persona.toLowerCase();

    if (!persona || normalised === "default" || normalised === "none" || normalised === "unknown") {
      return null;
    }

    const personaLabels = {
      collector: "Collector",
      historian: "Historian",
      explorer: "Explorer",
      investor: "Investor",
      hunter: "Hunter",
      drinker: "Drinker"
    };

    const modifier = normalised.replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    const label = personaLabels[normalised] || persona.replace(/\b\w/g, (char) => char.toUpperCase());

    return { label, modifier };
  };

  // Public reviewer name. Guests keep their own display name. Members use the backend's
  // privacy-safe publicReviewerName ("Sachin M." - first name plus last-name initial, never the
  // full surname), or just the first name from an older backend. Never an invented initial, and
  // never anything derived from an email address.
  const getReviewerName = (review) => {
    const publicName = String(getValue(review, "publicReviewerName", "") || "").trim();
    const firstName = String(getValue(review, "customerFirstName", "") || "").trim();
    const displayName = String(getValue(review, "reviewerDisplayName", "") || "").trim();
    const reviewSource = getExplicitReviewSource(review);

    if (reviewSource === "guest" && displayName) {
      return displayName;
    }

    if (publicName) {
      return publicName;
    }

    if (firstName && !firstName.includes("@")) {
      return firstName;
    }

    if (reviewSource === "guest") {
      return "Guest Customer";
    }

    if (reviewSource === "member") {
      return "Archive Member";
    }

    return "Archive Customer";
  };

  const getReviewerAvatarUrl = (review) => {
    // The member's own uploaded Archive profile photo, as a signed https URL from the API. Never
    // used for guest reviews, and never derived from an email, Gravatar or any external identity.
    if (getExplicitReviewSource(review) === "guest") {
      return "";
    }

    const url = String(getValue(review, "reviewerAvatarUrl", "") || "").trim();
    return /^https:\/\//i.test(url) ? url : "";
  };

  // Avatar markup: the photo when there is one, with the existing first-letter initial kept
  // underneath as the fallback if the photo is missing or fails to load.
  const renderReviewerAvatar = (review, initial) => {
    const avatarUrl = getReviewerAvatarUrl(review);

    if (!avatarUrl) {
      return `<span class="archive-review-card__avatar">${escapeHtml(initial)}</span>`;
    }

    return `
          <span class="archive-review-card__avatar archive-review-card__avatar--photo">
            <span class="archive-review-card__avatar-initial" aria-hidden="true">${escapeHtml(initial)}</span>
            <img class="archive-review-card__avatar-image" src="${escapeHtml(avatarUrl)}" alt="" width="42" height="42" loading="lazy" decoding="async" referrerpolicy="no-referrer" data-archive-reviewer-avatar>
          </span>`;
  };

  // A failed avatar (e.g. an expired signed URL) is removed so the initial shows - never a
  // broken-image icon. Image error events don't bubble, hence the capture listener.
  root.addEventListener("error", (event) => {
    const image = event.target;
    if (!(image instanceof HTMLImageElement) || !image.matches("[data-archive-reviewer-avatar]")) {
      return;
    }

    image.closest(".archive-review-card__avatar")?.classList.remove("archive-review-card__avatar--photo");
    image.remove();
  }, true);

  const isReviewBodyClampEnabled = () =>
    window.matchMedia("(min-width: 681px)").matches;

  function updateReviewBodyClampState() {
    if (!els.list) {
      return;
    }

    const canClamp =
      isReviewBodyClampEnabled();

    els.list.querySelectorAll(".archive-review-card").forEach((card) => {
      const body =
        card.querySelector(".archive-review-card__body");
      const button =
        card.querySelector(".archive-review-card__read-more");

      if (!body || !button) {
        return;
      }

      if (!canClamp) {
        body.classList.remove("is-clamped");
        button.hidden = true;
        return;
      }

      const styles =
        window.getComputedStyle(body);
      const lineHeight =
        Number.parseFloat(styles.lineHeight) ||
        Number.parseFloat(styles.fontSize) * 1.55;
      const maxHeight =
        lineHeight * 3;
      const hasOverflow =
        body.scrollHeight > maxHeight + 1;

      body.classList.toggle("is-clamped", hasOverflow);
      button.hidden = !hasOverflow;
    });
  }

  const endpoint = (key) => config.endpoints && config.endpoints[key];

  const browserId = getOrCreateBrowserId();

  function getOrCreateBrowserId() {
    const key = "archiveReviewHelpfulBrowserId";

    try {
      const existing = window.localStorage && window.localStorage.getItem(key);
      if (existing) {
        return existing;
      }

      const generated =
        window.crypto && window.crypto.randomUUID
          ? window.crypto.randomUUID()
          : `browser-${Date.now()}-${Math.random().toString(16).slice(2)}`;

      window.localStorage && window.localStorage.setItem(key, generated);
      return generated;
    } catch (error) {
      return `session-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
  }

  function withBrowserId(url) {
    if (!url || !browserId) {
      return url;
    }

    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}browserId=${encodeURIComponent(browserId)}`;
  }

  const reviewStatus = (review) =>
    String(getValue(review, "status", "")).toLowerCase();

  const normalizedReviewStatus = (review) =>
    reviewStatus(review).replace(/[^a-z0-9]/g, "");

  const normalizeProductId = (value) =>
    String(value ?? "")
      .trim()
      .replace(/^gid:\/\/shopify\/Product\//i, "");

  const reviewMatchesCurrentProduct = (review) => {
    const currentProductId = normalizeProductId(config.productId);

    return [getValue(review, "productId", ""), getValue(review, "productGid", "")]
      .some((value) => normalizeProductId(value) === currentProductId);
  };

  const getCurrentCustomerReview = () =>
    state.myReviews
      .filter((review) =>
        reviewMatchesCurrentProduct(review) &&
        !["rejected", "deleted"].includes(normalizedReviewStatus(review))
      )
      .sort((a, b) => reviewTimestamp(b) - reviewTimestamp(a))[0];

  const isBlockingCustomerReview = (review) => {
    const status = normalizedReviewStatus(review);
    return ["pending", "pendingapproval", "underreview", "published"].includes(status);
  };

  const reviewTimestamp = (review) => {
    const date =
      getValue(review, "updatedAtUtc") ||
      getValue(review, "publishedAtUtc") ||
      getValue(review, "createdAtUtc") ||
      "";

    const time = new Date(date).getTime();
    return Number.isNaN(time) ? 0 : time;
  };

  const directReviewUrl = (review) => {
    const url = new URL(window.location.href);
    const handle = getValue(review, "productHandle", config.productHandle || "");
    if (handle) {
      url.pathname = `/products/${handle}`;
    }
    url.hash = `archive-review-${encodeURIComponent(getValue(review, "reviewId", ""))}`;
    return url.toString();
  };

  const getImageContentType = (file) => {
    if (allowedImageTypes.includes(file.type)) {
      return file.type;
    }

    const extension = String(file.name || "")
      .split(".")
      .pop()
      .toLowerCase();

    return imageTypeByExtension[extension] || "";
  };

  function resolveEndpointUrl(url) {
    if (!url) {
      throw new Error("Archive reviews endpoint URL is missing.");
    }

    return new URL(url, window.location.origin).toString();
  }

  async function fetchJson(url, options) {
    const requestUrl = resolveEndpointUrl(url);
    const response = await fetch(requestUrl, {
      credentials: "same-origin",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json"
      },
      ...options
    });
    const contentType = response.headers.get("content-type") || "";

    if (!response.ok) {
      const text = await response.text();
      const summary = text ? `: ${text.slice(0, 180).replace(/\s+/g, " ").trim()}` : "";
      const error = new Error(
        `Request failed (${response.status}) for ${requestUrl}. Content-Type: ${contentType || "unknown"}${summary}`
      );
      error.status = response.status;
      error.contentType = contentType;
      error.responseText = text;
      error.requestUrl = requestUrl;
      throw error;
    }

    const text = await response.text();

    if (!contentType.toLowerCase().includes("application/json")) {
      try {
        return JSON.parse(text);
      } catch (parseError) {
        // Some App Proxy failures arrive as plain text/HTML. Keep the technical
        // details on the Error for console logging, but never show them raw.
      }

      const error = new Error(
        `Expected JSON from ${requestUrl}, received ${contentType || "unknown"} at ${response.url || requestUrl}.`
      );
      error.status = response.status;
      error.contentType = contentType;
      error.responseText = text;
      error.requestUrl = requestUrl;
      throw error;
    }

    return text ? JSON.parse(text) : {};
  }

  function getCustomerFormError(error) {
    const responseText = String(error && error.responseText ? error.responseText : "").trim();

    if (responseText && !/^</.test(responseText)) {
      const friendlyMessage = responseText.replace(/\s+/g, " ");

      if (/^\s*[\[{]/.test(responseText)) {
        return "Unable to submit your review. Please check your details and try again.";
      }

      if (/review must contain at least 50 words\.?/i.test(friendlyMessage)) {
        return "Please write at least 50 words before submitting your review.";
      }

      if (/already submitted a review for this product\.?/i.test(friendlyMessage)) {
        return "Your review has already been received and is awaiting approval.";
      }

      return friendlyMessage;
    }

    return "Unable to submit your review. Please check your details and try again.";
  }

  function getCustomerUploadError(error, file) {
    const responseText = String(error && error.responseText ? error.responseText : "").trim();
    const normalized = responseText.toLowerCase();
    const fileIsTooLarge = file && file.size > maxImageSizeBytes;

    if (normalized.includes("maximum of 4") || normalized.includes("between 1 and 4")) {
      return "A review can include a maximum of 4 images.";
    }

    if (normalized.includes("not supported")) {
      return "Please upload JPG, PNG, WebP or HEIC images up to 8MB each.";
    }

    if (
      fileIsTooLarge ||
      normalized.includes("cannot exceed 8 mb") ||
      normalized.includes("cannot exceed 8mb")
    ) {
      return "Please upload images up to 8MB each.";
    }

    return "We couldn't upload that photo. Please try again.";
  }

  function updateSummary() {
    const count = state.reviews.length;
    const total = state.reviews.reduce(
      (sum, review) => sum + (Number(getValue(review, "rating", 0)) || 0),
      0
    );
    const average = count ? total / count : 0;
    const withPhotos = state.reviews.filter((review) => getReviewImages(review).length > 0).length;
    const verified = state.reviews.filter(isVerifiedReview).length;

    els.average.textContent = count ? average.toFixed(1) : "0.0";
    els.stars.textContent = starString(average);
    els.count.textContent = count === 1 ? "Based on 1 Archive Review" : `Based on ${count} Archive Reviews`;
    els.photoStat.textContent = count ? `${Math.round((withPhotos / count) * 100)}%` : "0%";
    els.memberStat.textContent = String(count);
    els.verifiedStat.textContent = count && verified ? `${Math.round((verified / count) * 100)}%` : "\u2014";
  }

  function setLoading(isLoading) {
    state.isLoading = isLoading;
    root.classList.toggle("archive-reviews--loading", isLoading);
    root.classList.toggle("archive-reviews--guest-empty", isLoading && !config.isAuthenticated);

    if (isLoading) {
      els.memberStatus.hidden = true;
      els.empty.hidden = true;
      if (els.guestEmpty) {
        els.guestEmpty.hidden = config.isAuthenticated;
      }
      if (els.guestRewardGuide) {
        els.guestRewardGuide.hidden = config.isAuthenticated;
      }
      if (els.guestInfo) {
        els.guestInfo.hidden = config.isAuthenticated;
      }
      if (els.guestModerationNote) {
        els.guestModerationNote.hidden = config.isAuthenticated;
      }
      els.pagination.hidden = true;
      els.list.hidden = !config.isAuthenticated;
      els.list.innerHTML = `
        <article class="archive-review-card archive-review-card--skeleton" aria-hidden="true">
          <div class="archive-review-skeleton-reviewer">
            <span></span>
            <div>
              <i></i>
              <i></i>
              <i></i>
            </div>
          </div>
          <div class="archive-review-skeleton-content">
            <i></i>
            <i></i>
            <i></i>
            <i></i>
          </div>
        </article>
      `;
    }
  }

  function getFilteredReviews() {
    const filtered = state.reviews.filter((review) => {
      const rating = Number(getValue(review, "rating", 0));
      const images = getReviewImages(review);

      if (state.filters.rating !== "all" && rating !== Number(state.filters.rating)) {
        return false;
      }

      if (state.filters.photos === "photos" && images.length === 0) {
        return false;
      }

      return true;
    });

    filtered.sort((a, b) => {
      if (state.filters.sort === "highest") {
        return Number(getValue(b, "rating", 0)) - Number(getValue(a, "rating", 0));
      }

      if (state.filters.sort === "lowest") {
        return Number(getValue(a, "rating", 0)) - Number(getValue(b, "rating", 0));
      }

      if (state.filters.sort === "helpful") {
        return Number(getValue(b, "helpfulCount", 0)) - Number(getValue(a, "helpfulCount", 0));
      }

      return new Date(getValue(b, "publishedAtUtc", getValue(b, "createdAtUtc", 0))) -
        new Date(getValue(a, "publishedAtUtc", getValue(a, "createdAtUtc", 0)));
    });

    return filtered;
  }

  function renderReviewCard(review) {
    const rating = Number(getValue(review, "rating", 0)) || 0;
    const images = getReviewImages(review);
    const reviewer = getReviewerName(review);
    const initial = reviewer.charAt(0).toUpperCase() || "A";
    const badge = getReviewerBadge(review);
    const persona = getReviewerPersona(review);
    const date = formatDate(getValue(review, "publishedAtUtc", getValue(review, "createdAtUtc", "")));
    const reviewId = String(getValue(review, "reviewId", ""));
    const title = getValue(review, "title", "Archive review");
    const body = getValue(review, "reviewBody", getValue(review, "body", ""));
    const isExpanded = state.expandedReviewIds.has(reviewId);
    const helpfulCount = Number(getValue(review, "helpfulCount", 0)) || 0;
    const hasMarkedHelpful = Boolean(getValue(review, "hasMarkedHelpful", false));
    const menuOpen = state.openMenuReviewId === reviewId;

    return `
      <article class="archive-review-card" id="archive-review-${escapeHtml(reviewId)}" data-review-id="${escapeHtml(reviewId)}">
        <div class="archive-review-card__reviewer">
          ${renderReviewerAvatar(review, initial)}
          <div class="archive-review-card__reviewer-meta">
            <p class="archive-review-card__name">${escapeHtml(reviewer)}</p>
            <div class="archive-review-card__badges">
              ${badge
                ? `<span class="archive-review-card__badge archive-review-card__badge--${escapeHtml(badge.modifier)}">
                    ${badge.icon}
                    ${escapeHtml(badge.label)}
                  </span>`
                : ""}
              ${persona ? `<span class="archive-review-card__persona archive-review-card__persona--${escapeHtml(persona.modifier)}">${escapeHtml(persona.label)}</span>` : ""}
            </div>
            <div class="archive-review-card__date">${escapeHtml(date)}</div>
          </div>
        </div>
        <div class="archive-review-card__main">
          <div class="archive-review-card__top">
            <div class="archive-review-card__headline">
              <div class="archive-review-card__stars" aria-label="${rating} out of 5 stars">${starString(rating)}</div>
              <h3 class="archive-review-card__title" title="${escapeHtml(title)}">${escapeHtml(title)}</h3>
            </div>
            <div class="archive-review-card__menu">
              <button type="button" data-review-menu="${escapeHtml(reviewId)}" aria-label="Review options" aria-expanded="${menuOpen ? "true" : "false"}">
                <span aria-hidden="true"></span>
                <span aria-hidden="true"></span>
                <span aria-hidden="true"></span>
              </button>
              <div class="archive-review-card__menu-panel" ${menuOpen ? "" : "hidden"} role="menu">
                <button type="button" data-share-review="${escapeHtml(reviewId)}" role="menuitem">Share review</button>
              </div>
            </div>
          </div>
          <p class="archive-review-card__body ${isExpanded ? "is-expanded" : ""}">${escapeHtml(body)}</p>
          <button type="button" class="archive-review-card__read-more" data-review-read-more="${escapeHtml(reviewId)}" aria-expanded="${isExpanded ? "true" : "false"}" hidden>${isExpanded ? "Show less" : "Read more"}</button>
          ${images.length
            ? `<div class="archive-review-card__images">${images
                .map((image, imageIndex) => {
                  const url = getReviewImageUrl(image);
                  return url ? `<button type="button" data-preview-review-image="${escapeHtml(reviewId)}" data-preview-review-image-index="${imageIndex}" aria-label="Preview review image"><img src="${escapeHtml(url)}" alt="" loading="lazy" data-review-thumbnail></button>` : "";
                })
                .join("")}</div>`
            : ""}
          <div class="archive-review-card__helpful">
            <span>Was this review helpful?</span>
            <button type="button" data-helpful-review="${escapeHtml(reviewId)}" class="${hasMarkedHelpful ? "is-active" : ""}" aria-pressed="${hasMarkedHelpful ? "true" : "false"}">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6.3 10.4h3v9.1h-3a1.3 1.3 0 0 1-1.3-1.3v-6.5a1.3 1.3 0 0 1 1.3-1.3Z"></path>
                <path d="M9.3 10.4 12.2 4.6a1.8 1.8 0 0 1 3.4.9v3.2h3.2a2 2 0 0 1 1.9 2.4l-1.2 5.6a3.4 3.4 0 0 1-3.3 2.8H9.3"></path>
              </svg>
              Helpful (${helpfulCount})
            </button>
          </div>
        </div>
      </article>
    `;
  }

  function renderPagination(totalPages) {
    if (totalPages <= 1) {
      els.pagination.hidden = true;
      return;
    }

    els.pagination.hidden = false;
    els.pages.innerHTML = "";

    for (let page = 1; page <= totalPages; page += 1) {
      const button = document.createElement("button");
      button.type = "button";
      button.textContent = String(page);
      button.className = page === state.page ? "is-active" : "";
      button.addEventListener("click", () => {
        state.page = page;
        renderReviews();
      });
      els.pages.appendChild(button);
    }

    const previous = els.pagination.querySelector('[data-review-page="prev"]');
    const next = els.pagination.querySelector('[data-review-page="next"]');
    previous.disabled = state.page <= 1;
    next.disabled = state.page >= totalPages;
  }

  function renderReviews() {
    if (state.isLoading) {
      return;
    }

    const hasNoPublishedReviews = state.reviews.length === 0;
    const isGuestNoReviews = !config.isAuthenticated && hasNoPublishedReviews;
    root.classList.toggle("archive-reviews--guest-empty", isGuestNoReviews);

    if (els.toolbar) {
      els.toolbar.hidden = hasNoPublishedReviews;
    }
    if (els.guestRewardGuide) {
      els.guestRewardGuide.hidden = !isGuestNoReviews;
    }
    if (els.guestInfo) {
      els.guestInfo.hidden = !isGuestNoReviews;
    }
    if (els.rewardGuide) {
      els.rewardGuide.hidden = isGuestNoReviews;
    }
    if (els.guestEmpty) {
      els.guestEmpty.hidden = !isGuestNoReviews;
    }
    if (els.guestModerationNote) {
      els.guestModerationNote.hidden = !isGuestNoReviews;
    }

    if (isGuestNoReviews) {
      els.list.innerHTML = "";
      els.list.hidden = true;
      els.empty.hidden = true;
      els.pagination.hidden = true;
      els.clearFilters.hidden = true;
      return;
    }

    els.list.hidden = false;

    const filtered = getFilteredReviews();
    const totalPages = Math.max(1, Math.ceil(filtered.length / state.perPage));
    state.page = Math.min(state.page, totalPages);

    const start = (state.page - 1) * state.perPage;
    const pageReviews = filtered.slice(start, start + state.perPage);

    els.list.innerHTML = pageReviews.map(renderReviewCard).join("");
    updateReviewImageLoadStates();
    updateReviewBodyClampState();
    els.list.hidden = filtered.length === 0;
    els.empty.hidden = filtered.length !== 0;

    const current = getCurrentCustomerReview();
    const hasSubmittedReview =
      current &&
      reviewStatus(current) !== "published";

    root.querySelectorAll("[data-empty-review-cta]").forEach((button) => {
      button.hidden = Boolean(hasSubmittedReview);
    });

    renderPagination(totalPages);

    const hasFilters =
      state.filters.rating !== "all" ||
      state.filters.photos !== "all" ||
      state.filters.sort !== "newest";
    els.clearFilters.hidden = !hasFilters;
  }

  function updateReviewImageLoadStates() {
    els.list.querySelectorAll("[data-review-thumbnail]").forEach((image) => {
      const markLoaded = () => image.classList.add("is-loaded");

      if (image.complete && image.naturalWidth > 0) {
        markLoaded();
        return;
      }

      image.addEventListener("load", markLoaded, { once: true });
    });
  }

  function updateReviewHelpfulState(reviewId, hasMarkedHelpful, helpfulCount) {
    const review = state.reviews.find((item) => String(getValue(item, "reviewId", "")) === String(reviewId));
    if (!review) {
      return;
    }

    review.hasMarkedHelpful = hasMarkedHelpful;
    review.HasMarkedHelpful = hasMarkedHelpful;
    review.helpfulCount = helpfulCount;
    review.HelpfulCount = helpfulCount;
    renderReviews();
  }

  async function toggleHelpful(reviewId) {
    if (!endpoint("helpfulVote")) {
      showToast("Helpful voting is unavailable right now.");
      return;
    }

    const response = await fetchJson(withBrowserId(`${endpoint("helpfulVote")}?reviewId=${encodeURIComponent(reviewId)}`), {
      method: "POST",
      body: "{}"
    });

    const hasMarkedHelpful =
      Boolean(getValue(response, "hasMarkedHelpful", false));

    updateReviewHelpfulState(
      reviewId,
      hasMarkedHelpful,
      Number(getValue(response, "helpfulCount", 0)) || 0);

    showToast(hasMarkedHelpful ? "Thanks for your feedback!" : "Helpful vote removed.");
  }

  function setOpenMenu(reviewId) {
    state.openMenuReviewId = state.openMenuReviewId === reviewId ? null : reviewId;
    renderReviews();
  }

  function closeReviewMenu() {
    if (!state.openMenuReviewId) {
      return;
    }

    state.openMenuReviewId = null;
    renderReviews();
  }

  function showToast(message) {
    let toast = root.querySelector("[data-review-toast]");

    if (!toast) {
      toast = document.createElement("div");
      toast.className = "archive-review-toast";
      toast.setAttribute("data-review-toast", "");
      root.appendChild(toast);
    }

    toast.textContent = message;
    toast.hidden = false;
    window.clearTimeout(showToast.timeoutId);
    showToast.timeoutId = window.setTimeout(() => {
      toast.hidden = true;
    }, 2600);
  }

  async function shareReview(reviewId) {
    const review = state.reviews.find((item) => String(getValue(item, "reviewId", "")) === String(reviewId));
    if (!review) {
      return;
    }

    const url = directReviewUrl(review);
    const title = getValue(review, "title", "Archive review");

    if (navigator.share) {
      await navigator.share({
        title,
        url
      });
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(url);
      showToast("Review link copied.");
    } else {
      window.prompt("Review link", url);
    }

    closeReviewMenu();
  }

  function openImagePreview(reviewId, index) {
    const review = state.reviews.find((item) => String(getValue(item, "reviewId", "")) === String(reviewId));
    const images = review ? getReviewImages(review).map(getReviewImageUrl).filter(Boolean) : [];

    if (!images.length) {
      return;
    }

    state.imagePreview = {
      images,
      index: Math.max(0, Math.min(index, images.length - 1))
    };

    const modal = document.createElement("div");
    modal.className = "archive-review-image-preview";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.setAttribute("aria-label", "Review image preview");

    let touchStartX = 0;
    let touchStartY = 0;

    const close = () => {
      document.removeEventListener("keydown", onKeydown);
      modal.classList.remove("is-visible");
      window.setTimeout(() => {
        modal.remove();
      }, 220);
    };

    const preloadAdjacent = () => {
      const count = state.imagePreview.images.length;

      if (count <= 1) {
        return;
      }

      [-1, 1].forEach((step) => {
        const nextIndex = state.imagePreview.index + step;

        if (nextIndex < 0 || nextIndex >= count) {
          return;
        }

        const image = new Image();
        image.src = state.imagePreview.images[nextIndex];
      });
    };

    const render = () => {
      const currentUrl = state.imagePreview.images[state.imagePreview.index];
      const hasMultiple = state.imagePreview.images.length > 1;
      const currentNumber = state.imagePreview.index + 1;
      const total = state.imagePreview.images.length;
      const isFirst = state.imagePreview.index === 0;
      const isLast = state.imagePreview.index === total - 1;

      modal.innerHTML = `
        <button type="button" class="archive-review-image-preview__close" data-preview-close aria-label="Close image preview">&times;</button>
        <div class="archive-review-image-preview__stage">
          ${hasMultiple ? `<button type="button" class="archive-review-image-preview__nav archive-review-image-preview__nav--prev archive-review-image-preview__nav--desktop" data-preview-prev aria-label="Previous image" ${isFirst ? "disabled" : ""}>&larr;</button>` : ""}
          <figure class="archive-review-image-preview__frame">
            <div class="archive-review-image-preview__image-wrap">
              ${hasMultiple ? `<button type="button" class="archive-review-image-preview__nav archive-review-image-preview__nav--prev archive-review-image-preview__nav--mobile" data-preview-prev aria-label="Previous image" ${isFirst ? "disabled" : ""}>&larr;</button>` : ""}
              <img src="${escapeHtml(currentUrl)}" alt="" data-preview-image>
              ${hasMultiple ? `<button type="button" class="archive-review-image-preview__nav archive-review-image-preview__nav--next archive-review-image-preview__nav--mobile" data-preview-next aria-label="Next image" ${isLast ? "disabled" : ""}>&rarr;</button>` : ""}
            </div>
            ${hasMultiple ? `<figcaption class="archive-review-image-preview__count">${currentNumber} / ${total}</figcaption>` : ""}
          </figure>
          ${hasMultiple ? `<button type="button" class="archive-review-image-preview__nav archive-review-image-preview__nav--next archive-review-image-preview__nav--desktop" data-preview-next aria-label="Next image" ${isLast ? "disabled" : ""}>&rarr;</button>` : ""}
        </div>
      `;

      const previewImage = modal.querySelector("[data-preview-image]");
      const frame = modal.querySelector(".archive-review-image-preview__frame");
      const markLoaded = () => frame?.classList.add("is-loaded");

      if (previewImage?.complete && previewImage.naturalWidth > 0) {
        markLoaded();
      } else {
        previewImage?.addEventListener("load", markLoaded, { once: true });
      }

      preloadAdjacent();
    };

    const move = (step) => {
      const count = state.imagePreview.images.length;
      if (count <= 1) {
        return;
      }

      const nextIndex = state.imagePreview.index + step;

      if (nextIndex < 0 || nextIndex >= count) {
        return;
      }

      state.imagePreview.index = nextIndex;
      render();
    };

    const onKeydown = (event) => {
      if (event.key === "Escape") {
        close();
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        move(-1);
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        move(1);
      }
    };

    modal.addEventListener("click", (event) => {
      if (event.target === modal || event.target.closest("[data-preview-close]")) {
        close();
        return;
      }

      if (event.target.closest("[data-preview-prev]")) {
        move(-1);
        return;
      }

      if (event.target.closest("[data-preview-next]")) {
        move(1);
      }
    });

    modal.addEventListener("touchstart", (event) => {
      if (!event.touches.length) {
        return;
      }

      touchStartX = event.touches[0].clientX;
      touchStartY = event.touches[0].clientY;
    }, { passive: true });

    modal.addEventListener("touchend", (event) => {
      if (!event.changedTouches.length) {
        return;
      }

      const deltaX = event.changedTouches[0].clientX - touchStartX;
      const deltaY = event.changedTouches[0].clientY - touchStartY;

      if (Math.abs(deltaX) < 44 || Math.abs(deltaX) < Math.abs(deltaY)) {
        return;
      }

      move(deltaX > 0 ? -1 : 1);
    }, { passive: true });

    document.addEventListener("keydown", onKeydown);
    render();
    document.body.appendChild(modal);
    window.requestAnimationFrame(() => {
      modal.classList.add("is-visible");
      modal.querySelector("[data-preview-close]")?.focus();
    });
  }

  function renderMyReviewStatus() {
    if (!config.isAuthenticated || !els.memberStatus) {
      return;
    }

    const current = getCurrentCustomerReview();
    const openButton = root.querySelector("[data-open-review-modal]");
    const actionHelper = root.querySelector("[data-review-action] > p");

    if (openButton && !openButton.dataset.defaultHtml) {
      openButton.dataset.defaultHtml = openButton.innerHTML;
    }

    if (actionHelper && !actionHelper.dataset.defaultText) {
      actionHelper.dataset.defaultText = actionHelper.textContent || "";
    }

    const restoreWriteReviewAction = () => {
      if (openButton) {
        openButton.hidden = false;
        openButton.disabled = false;
        openButton.removeAttribute("aria-disabled");
        openButton.classList.remove("archive-review-button--disabled");
        if (openButton.dataset.defaultHtml) {
          openButton.innerHTML = openButton.dataset.defaultHtml;
        }
      }

      if (actionHelper && actionHelper.dataset.defaultText) {
        actionHelper.textContent = actionHelper.dataset.defaultText;
      }
    };

    if (!current) {
      els.memberStatus.hidden = true;
      restoreWriteReviewAction();
      if (els.rewardGuide) {
        els.rewardGuide.hidden = false;
      }
      return;
    }

    const status = normalizedReviewStatus(current);
    const isPending = ["pending", "pendingapproval", "underreview"].includes(status);
    const isPublished = status === "published";

    if (!isPending && !isPublished) {
      els.memberStatus.hidden = true;
      restoreWriteReviewAction();
      if (els.rewardGuide) {
        els.rewardGuide.hidden = false;
      }
      return;
    }

    els.memberStatus.hidden = true;

    if (openButton && isPublished) {
      openButton.hidden = true;
    }

    if (openButton && isPending) {
      openButton.hidden = false;
      openButton.disabled = true;
      openButton.setAttribute("aria-disabled", "true");
      openButton.classList.add("archive-review-button--disabled");
      openButton.textContent = "Pending Approval";
    }

    if (actionHelper && isPending) {
      actionHelper.textContent =
        "Your review has been received and is currently awaiting approval by our team. Eligible Reward Points will be awarded automatically after approval.";
    }

    if (actionHelper && isPublished) {
      actionHelper.textContent =
        "Thank you for sharing your experience with the Archive community. Your published review is now helping fellow whisky enthusiasts discover this bottle.";
    }

    if (els.rewardGuide) {
      els.rewardGuide.hidden = true;
    }
  }

  function getReviewsFromResponse(response) {
    const reviews = getValue(response, "reviews", []);

    if (Array.isArray(reviews)) {
      return reviews;
    }

    const review = getValue(response, "review", null);
    return review ? [review] : [];
  }

  async function loadMyReviews(options = {}) {
    if (!config.isAuthenticated || !endpoint("myReviews")) {
      state.myReviews = [];
      state.myReviewsLoaded = true;
      return true;
    }

    try {
      const response = await fetchJson(endpoint("myReviews"));
      state.myReviews = getReviewsFromResponse(response);
      state.myReviewsLoaded = true;
      return true;
    } catch (error) {
      state.myReviews = [];
      state.myReviewsLoaded = false;
      console.error("Archive customer reviews failed to load", error);

      if (!options.silent) {
        showToast("Unable to check your review status. Please try again.");
      }

      return false;
    }
  }

  async function loadReviews() {
    try {
      const [productResponse] = await Promise.all([
        fetchJson(withBrowserId(endpoint("productReviews"))),
        loadMyReviews({ silent: true })
      ]);

      state.reviews = getValue(productResponse, "reviews", []);
    } catch (error) {
      state.reviews = [];
      state.myReviews = [];
      state.myReviewsLoaded = false;
      els.empty.querySelector("h3").textContent = "Reviews are unavailable";
      els.empty.querySelector("p").textContent = "Please refresh the page or try again shortly.";
      console.error("Archive reviews failed to load", error);
    }

    setLoading(false);
    updateSummary();
    renderMyReviewStatus();
    renderReviews();
  }

  function openModal(name, trigger) {
    const modal = root.querySelector(`[data-review-modal="${name}"]`);
    if (!modal) {
      return;
    }

    if (modalCloseTimer) {
      window.clearTimeout(modalCloseTimer);
      modalCloseTimer = null;
    }

    lastModalTrigger = trigger || document.activeElement;
    modal.hidden = false;
    modal.classList.remove("archive-review-modal--closing");
    document.body.classList.add("archive-review-modal-open");

    window.requestAnimationFrame(() => {
      modal.classList.add("archive-review-modal--open");
      const focusTarget = modal.querySelector("button, [href], input, textarea, select, [tabindex]:not([tabindex='-1'])");
      if (focusTarget) {
        focusTarget.focus({ preventScroll: true });
      }
    });
  }

  function closeModals() {
    const openModals = Array.from(root.querySelectorAll("[data-review-modal]")).filter((modal) => !modal.hidden);
    const shouldResetForm = openModals.some((modal) => modal.dataset.reviewModal === "form");
    const shouldRefreshReviewState = shouldResetForm && state.reviewSubmittedSuccessfully;

    openModals.forEach((modal) => {
      modal.classList.remove("archive-review-modal--open");
      modal.classList.add("archive-review-modal--closing");
    });

    document.body.classList.remove("archive-review-modal-open");

    modalCloseTimer = window.setTimeout(() => {
      openModals.forEach((modal) => {
        modal.hidden = true;
        modal.classList.remove("archive-review-modal--closing");
      });

      if (shouldResetForm) {
        resetForm();
      }

      if (shouldRefreshReviewState) {
        loadReviews();
      }

      if (lastModalTrigger && typeof lastModalTrigger.focus === "function") {
        lastModalTrigger.focus({ preventScroll: true });
      }

      lastModalTrigger = null;
      modalCloseTimer = null;
    }, 240);
  }

  function trapModalFocus(event) {
    const modal = root.querySelector(".archive-review-modal--open");
    if (!modal) {
      return;
    }

    const focusable = Array.from(
      modal.querySelectorAll("button, [href], input, textarea, select, [tabindex]:not([tabindex='-1'])")
    ).filter((element) => !element.disabled && element.offsetParent !== null);

    if (!focusable.length) {
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function setFormMessage(message, isSuccess) {
    if (!els.formMessage) {
      return;
    }

    els.formMessage.toggleAttribute("data-has-message", Boolean(message));
    els.formMessage.textContent = message || "";
    els.formMessage.style.color = isSuccess ? "#287c42" : "#9a4a40";
  }

  function setSubmitting(isSubmitting) {
    if (!els.formSubmitButton) {
      return;
    }

    if (!els.formSubmitButton.dataset.defaultText) {
      els.formSubmitButton.dataset.defaultText = els.formSubmitButton.textContent;
    }

    els.formSubmitButton.disabled = isSubmitting;
    els.formSubmitButton.textContent = isSubmitting
      ? "Submitting..."
      : els.formSubmitButton.dataset.defaultText;
  }

  function setSuccessState(isSuccess) {
    if (!els.form) {
      return;
    }

    els.form.classList.toggle("archive-review-form--submitted", isSuccess);

    if (els.formSuccess) {
      els.formSuccess.setAttribute("aria-hidden", isSuccess ? "false" : "true");
    }

    if (els.formContent) {
      els.formContent.setAttribute("aria-hidden", isSuccess ? "true" : "false");
      els.formContent.querySelectorAll("input, textarea, button").forEach((control) => {
        control.disabled = isSuccess;
      });
    }

    if (isSuccess) {
      const successFocusTarget = els.formSuccess && els.formSuccess.querySelector("button");
      if (successFocusTarget) {
        window.setTimeout(() => {
          successFocusTarget.focus({ preventScroll: true });
        }, 220);
      }
    }
  }

  async function prepareReview() {
    if (state.reviewId || !endpoint("startReview")) {
      return;
    }

    const response = await fetchJson(endpoint("startReview"), {
      method: "POST",
      body: "{}"
    });

    state.reviewId = getValue(response, "reviewId", "");
  }

  function updateRatingButtons() {
    root.querySelectorAll("[data-rating-value]").forEach((button) => {
      const value = Number(button.dataset.ratingValue);
      button.textContent = value <= state.rating ? "\u2605" : "\u2606";
    });
  }

  async function uploadFiles(files) {
    if (!files.length || !endpoint("uploadImage")) {
      return;
    }

    const selectedFiles = Array.from(files);
    const available = maxImagesPerReview - state.uploadedImages.length;

    if (selectedFiles.length > available) {
      setFormMessage("A review can include a maximum of 4 images.");
      return;
    }

    for (let index = 0; index < selectedFiles.length; index += 1) {
      const file = selectedFiles[index];
      const contentType = getImageContentType(file);

      if (!contentType || file.size > maxImageSizeBytes) {
        setFormMessage("Please upload JPG, PNG, WebP or HEIC images up to 8MB each.");
        return;
      }

      state.uploadedImages.push({
        localId: `${Date.now()}-${index}`,
        imageId: "",
        s3Key: "",
        fileName: file.name,
        contentType,
        sizeBytes: file.size,
        displayOrder: state.uploadedImages.length + 1,
        file,
        previewUrl:
          contentType === "image/heic" || contentType === "image/heif"
            ? ""
            : URL.createObjectURL(file),
        status: "queued",
        error: ""
      });
    }

    renderUploadPreview();

    await prepareReview();

    const pendingImages = state.uploadedImages.filter((image) => image.status === "queued");

    for (let index = 0; index < pendingImages.length; index += 1) {
      const image = pendingImages[index];
      image.status = "uploading";
      setFormMessage(`Uploading photo ${index + 1} of ${pendingImages.length}...`, true);
      renderUploadPreview();

      try {
        const metadata = await fetchJson(endpoint("uploadImage"), {
          method: "POST",
          body: JSON.stringify({
            reviewId: state.reviewId,
            fileName: image.fileName,
            contentType: image.contentType,
            sizeBytes: image.sizeBytes,
            displayOrder: image.displayOrder
          })
        });

        const uploadResponse = await fetch(getValue(metadata, "uploadUrl"), {
          method: "PUT",
          headers: {
            "Content-Type": image.contentType
          },
          body: image.file
        });

        if (!uploadResponse.ok) {
          throw new Error("Unable to upload this photo. Please try again.");
        }

        image.imageId = getValue(metadata, "imageId");
        image.s3Key = getValue(metadata, "s3Key");
        image.displayOrder = getValue(metadata, "displayOrder", image.displayOrder);
        image.status = "uploaded";
      } catch (error) {
        image.status = "error";
        image.error = "Upload failed";
        renderUploadPreview();
        error.uploadFile = image.file;
        throw error;
      }

      renderUploadPreview();
    }

    if (pendingImages.length) {
      setFormMessage("Photos uploaded successfully.", true);
    }
  }

  function renderUploadPreview() {
    const uploadSlots = root.querySelectorAll(".archive-review-upload--empty");
    uploadSlots.forEach((slot, index) => {
      const image = state.uploadedImages[index];
      if (image && image.previewUrl) {
        slot.classList.add("has-image");
        slot.innerHTML = `
          <img src="${escapeHtml(image.previewUrl)}" alt="">
          <span class="archive-review-upload__status">${escapeHtml(uploadStatusLabel(image))}</span>
          <span class="archive-review-upload__remove" data-remove-upload="${index}" aria-hidden="true">&times;</span>
        `;
      } else if (image) {
        slot.classList.add("has-image");
        slot.innerHTML = `
          <span class="archive-review-upload__file">${escapeHtml(uploadStatusLabel(image))}</span>
          <span class="archive-review-upload__remove" data-remove-upload="${index}" aria-hidden="true">&times;</span>
        `;
      } else {
        slot.classList.remove("has-image");
        slot.textContent = "+";
      }
      slot.setAttribute("aria-label", image ? `${image.fileName} uploaded` : "Add another photo");
    });
  }

  function uploadStatusLabel(image) {
    if (image.status === "uploading" || image.status === "queued") {
      return "Uploading";
    }

    if (image.status === "error") {
      return "Failed";
    }

    return "Uploaded";
  }

  function removeUploadedImage(index) {
    const image = state.uploadedImages[index];

    if (!image) {
      return;
    }

    if (image.previewUrl) {
      URL.revokeObjectURL(image.previewUrl);
    }

    state.uploadedImages.splice(index, 1);
    state.uploadedImages.forEach((item, itemIndex) => {
      item.displayOrder = itemIndex + 1;
    });
    renderUploadPreview();
    setFormMessage("");
  }

  async function submitReview(event) {
    event.preventDefault();
    setFormMessage("");

    const formData = new FormData(els.form);
    const reviewerDisplayName = String(formData.get("reviewerDisplayName") || "").trim();
    const title = String(formData.get("title") || "").trim();
    const body = String(formData.get("body") || "").trim();

    if (!state.rating) {
      setFormMessage("Please choose a rating.");
      return;
    }

    if (!config.isAuthenticated && !reviewerDisplayName) {
      setFormMessage("Please enter a display name.");
      return;
    }

    if (!config.isAuthenticated && reviewerDisplayName.length > 30) {
      setFormMessage("Display name must be 30 characters or less.");
      return;
    }

    if (state.uploadedImages.some((image) => image.status === "queued" || image.status === "uploading")) {
      setFormMessage("Please wait for photos to finish uploading.");
      return;
    }

    if (state.uploadedImages.some((image) => image.status === "error")) {
      setFormMessage("Please remove failed photos before submitting your review.");
      return;
    }

    const submittedImages = state.uploadedImages.map((image, index) => ({
      imageId: image.imageId,
      s3Key: image.s3Key,
      fileName: image.fileName,
      contentType: image.contentType,
      sizeBytes: image.sizeBytes,
      displayOrder: index + 1
    }));

    try {
      setSubmitting(true);
      await prepareReview();

      const payload = {
        reviewId: state.reviewId,
        productId: String(config.productId),
        rating: state.rating,
        title,
        body,
        images: submittedImages
      };

      if (!config.isAuthenticated) {
        payload.reviewerDisplayName = reviewerDisplayName;
      }

      await fetchJson(endpoint("submitReview"), {
        method: "POST",
        body: JSON.stringify(payload)
      });

      setFormMessage("");
      state.reviewSubmittedSuccessfully = true;
      setSuccessState(true);
      await loadReviews();
    } catch (error) {
      console.error("Archive review submission failed", error);

      if (/already submitted a review for this product\.?/i.test(String(error.responseText || ""))) {
        await loadReviews();
        closeModals();
        showToast("Your review is already pending approval.");
        return;
      }

      setFormMessage(getCustomerFormError(error));
      setSubmitting(false);
    }
  }

  function resetForm() {
    if (els.form) {
      els.form.reset();
    }
    state.uploadedImages.forEach((image) => {
      if (image.previewUrl) {
        URL.revokeObjectURL(image.previewUrl);
      }
    });
    state.reviewId = null;
    state.rating = 0;
    state.uploadedImages = [];
    state.reviewSubmittedSuccessfully = false;
    setSuccessState(false);
    setSubmitting(false);
    updateRatingButtons();
    renderUploadPreview();
    setFormMessage("");
    updateCounters();
  }

  function updateCounters() {
    const displayName = els.form && els.form.querySelector('input[name="reviewerDisplayName"]');
    const title = els.form && els.form.querySelector('input[name="title"]');
    const body = els.form && els.form.querySelector('textarea[name="body"]');
    const displayNameCount = root.querySelector("[data-display-name-count]");
    const titleCount = root.querySelector("[data-title-count]");
    const bodyCount = root.querySelector("[data-body-count]");

    if (displayName && displayNameCount) {
      displayNameCount.textContent = `${displayName.value.length}/30`;
    }

    if (title && titleCount) {
      titleCount.textContent = `${title.value.length}/80`;
    }

    if (body && bodyCount) {
      bodyCount.textContent = `${body.value.length}/2000`;
    }
  }

  function bindEvents() {
    root.querySelectorAll("[data-review-filter]").forEach((select) => {
      select.addEventListener("change", () => {
        state.filters[select.dataset.reviewFilter] = select.value;
        state.page = 1;
        renderReviews();
      });
    });

    els.clearFilters.addEventListener("click", () => {
      state.filters.rating = "all";
      state.filters.photos = "all";
      state.filters.sort = "newest";
      root.querySelectorAll("[data-review-filter]").forEach((select) => {
        select.value = state.filters[select.dataset.reviewFilter];
      });
      state.page = 1;
      renderReviews();
    });

    root.querySelector('[data-review-page="prev"]').addEventListener("click", () => {
      state.page = Math.max(1, state.page - 1);
      renderReviews();
    });

    root.querySelector('[data-review-page="next"]').addEventListener("click", () => {
      state.page += 1;
      renderReviews();
    });

    root.querySelectorAll("[data-close-review-modal]").forEach((button) => {
      button.addEventListener("click", closeModals);
    });

    root.querySelectorAll("[data-open-signin-modal]").forEach((button) => {
      button.addEventListener("click", (event) => openModal("signin", event.currentTarget));
    });

    root.querySelectorAll("[data-open-reviews-info]").forEach((button) => {
      button.addEventListener("click", (event) => openModal("info", event.currentTarget));
    });

    root.querySelectorAll("[data-open-review-modal]").forEach((button) => {
      button.addEventListener("click", async (event) => {
        if (button.disabled || button.getAttribute("aria-disabled") === "true") {
          return;
        }

        if (config.isAuthenticated) {
          const loaded = await loadMyReviews();
          renderMyReviewStatus();

          const current = getCurrentCustomerReview();
          if (!loaded || (current && isBlockingCustomerReview(current))) {
            return;
          }
        }

        try {
          await prepareReview();
          openModal("form", event.currentTarget);
        } catch (error) {
          console.error("Archive review start failed", error);
          showToast("Unable to start your review right now. Please try again.");
        }
      });
    });

    root.querySelectorAll("[data-review-success-signin]").forEach((button) => {
      button.addEventListener("click", () => {
        closeModals();
        window.setTimeout(() => {
          openModal("signin", button);
        }, 260);
      });
    });

    els.list.addEventListener("click", (event) => {
      const menuButton = event.target.closest("[data-review-menu]");
      if (menuButton) {
        event.stopPropagation();
        setOpenMenu(menuButton.dataset.reviewMenu);
        return;
      }

      const shareButton = event.target.closest("[data-share-review]");
      if (shareButton) {
        event.stopPropagation();
        shareReview(shareButton.dataset.shareReview).catch(() => {
          showToast("Unable to share this review.");
        });
        return;
      }

      const helpfulButton = event.target.closest("[data-helpful-review]");
      if (helpfulButton) {
        helpfulButton.disabled = true;
        toggleHelpful(helpfulButton.dataset.helpfulReview).catch((error) => {
          helpfulButton.disabled = false;
          showToast(error.message || "Unable to update helpful vote.");
        });
        return;
      }

      const readMoreButton = event.target.closest("[data-review-read-more]");
      if (readMoreButton) {
        const reviewId = String(readMoreButton.dataset.reviewReadMore || "");
        if (state.expandedReviewIds.has(reviewId)) {
          state.expandedReviewIds.delete(reviewId);
        } else {
          state.expandedReviewIds.add(reviewId);
        }
        renderReviews();
        return;
      }

      const previewButton = event.target.closest("[data-preview-review-image]");
      if (previewButton) {
        openImagePreview(
          previewButton.dataset.previewReviewImage,
          Number(previewButton.dataset.previewReviewImageIndex) || 0);
      }
    });

    root.querySelectorAll("[data-rating-value]").forEach((button) => {
      button.addEventListener("click", () => {
        state.rating = Number(button.dataset.ratingValue);
        updateRatingButtons();
      });
    });

    const fileInput = root.querySelector("[data-review-files]");
    if (fileInput) {
      fileInput.addEventListener("change", (event) => {
        uploadFiles(event.target.files).catch((error) => {
          console.error("Archive review image upload failed", error);
          setFormMessage(getCustomerUploadError(error, error.uploadFile));
        });
        event.target.value = "";
      });
    }

    root.querySelectorAll("[data-trigger-upload]").forEach((button) => {
      button.addEventListener("click", (event) => {
        const removeButton = event.target.closest("[data-remove-upload]");

        if (removeButton) {
          event.stopPropagation();
          removeUploadedImage(Number(removeButton.dataset.removeUpload));
          return;
        }

        fileInput && fileInput.click();
      });
    });

    if (els.form) {
      els.form.addEventListener("submit", submitReview);
      els.form.querySelectorAll("input, textarea").forEach((field) => {
        field.addEventListener("input", updateCounters);
      });
    }

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        closeReviewMenu();
        closeModals();
      } else if (event.key === "Tab") {
        trapModalFocus(event);
      }
    });

    document.addEventListener("click", (event) => {
      if (!event.target.closest(".archive-review-card__menu")) {
        closeReviewMenu();
      }
    });

    window.addEventListener("resize", updateReviewBodyClampState);
  }

  bindEvents();
  setLoading(true);
  loadReviews();
})();
